import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { clearSession, isAuthenticated } from "../utils/SessionHandler";
import { logout } from "../apis/auth";
import { resetLegacySalesFormStore } from "../services/useSalesFormStore";
import { clearMinimizedPages } from "./workspace/WorkspaceContext";

const AuthContext = createContext(null);

const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  VALID_ROUTES: "validRoutes",
  SIDEBAR_ITEMS: "sidebarItems",
  ROLE_NAME: "role_name",
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoggedIn(isAuthenticated());
    setIsLoading(false);
    const onExpired = () => {
      resetLegacySalesFormStore();
      clearMinimizedPages();
      setIsLoggedIn(false);
    };
    // A token replacement in another tab must not leave the previous user's
    // working form mounted while subsequent API calls use the new credentials.
    const onSessionChange = (event) => {
      if (event.key === STORAGE_KEYS.AUTH_TOKEN && event.oldValue !== event.newValue) {
        onExpired();
      }
    };
    window.addEventListener("erp-session-expired", onExpired);
    window.addEventListener("storage", onSessionChange);
    return () => {
      window.removeEventListener("erp-session-expired", onExpired);
      window.removeEventListener("storage", onSessionChange);
    };
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } finally {
      resetLegacySalesFormStore();
      clearMinimizedPages();
      setIsLoggedIn(false);
      clearSession();
      localStorage.removeItem(STORAGE_KEYS.VALID_ROUTES);
      localStorage.removeItem(STORAGE_KEYS.SIDEBAR_ITEMS);
      localStorage.removeItem(STORAGE_KEYS.ROLE_NAME);
    }
  }, []);

  const handleLogin = useCallback(() => {
    resetLegacySalesFormStore();
    clearMinimizedPages();
    setIsLoggedIn(true);
  }, []);

  const value = useMemo(
    () => ({ isLoggedIn, isLoading, handleLogout, handleLogin }),
    [isLoggedIn, isLoading, handleLogout, handleLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
